import { DailyRewardModal } from "@components/features/DailyRewardModal";
import { useDailyRewardModalTrigger } from "@hooks/useDailyRewardModalTrigger";
import useGetRewardsInfo from "@hooks/useGetRewardsInfo";
import usePostLoginInfo from "@hooks/usePostLoginInfo";
import { _MemberSettingOnboardDialogLayout } from "@types";
import logger from "@utils/logger";
import { useCallback } from "react";

/**
 * Handler component for Daily Login Modal
 * Manages modal state and handles post-claim actions (re-triggering name collection)
 * Similar to FastLoginHandler pattern - self-contained logic component
 */
const DailyRewardModalHandler = () => {
  const { isModalOpen, closeModal, wasTriggeredFromPostLoginStep } = useDailyRewardModalTrigger();
  const { isFulfilled: isPostLoginInfoFulfilled, triggerForm } = usePostLoginInfo();
  const { refetch: refetchRewardData } = useGetRewardsInfo();

  const handleClaimComplete = useCallback(() => {
    // After Daily Login modal closes, re-trigger post-login info step if modal was triggered from it
    if (wasTriggeredFromPostLoginStep && !isPostLoginInfoFulfilled) {
      // Small delay to ensure modal is fully closed
      setTimeout(() => {
        triggerForm("dialog", undefined, {
          layout: _MemberSettingOnboardDialogLayout.DIALOG_DRAWER,
        });
      }, 100);
    }
  }, [wasTriggeredFromPostLoginStep, isPostLoginInfoFulfilled, triggerForm]);

  const handleClaimError = useCallback(async () => {
    // On claim error, refetch promotion status first, then close modal
    // This ensures we have updated status before closing to prevent immediate reopen
    try {
      await refetchRewardData();
    } catch (error) {
      // Silently handle refetch errors - still close modal
      logger._console.error("[DailyRewardModalHandler] Error refetching reward data", error);
    } finally {
      closeModal();
    }
  }, [refetchRewardData, closeModal]);

  return (
    <DailyRewardModal
      isOpen={isModalOpen}
      onClose={closeModal}
      onClaimComplete={handleClaimComplete}
      onClaimError={handleClaimError}
    />
  );
};

export default DailyRewardModalHandler;
